/**
 * Magnification math for the dock.
 *
 * The macOS dock does NOT scale an icon by "distance to the pointer" directly.
 * If it did, wide items (a 2x-width widget, a minimized-window thumbnail) would
 * receive a scale that only matches their centre point and would visibly shear
 * against their neighbours.
 *
 * Instead we model magnification as a *magnification wave*: the pointer sits at
 * the crest of a sine hump whose half-period is `maxRange`. The scale of an item
 * is then the AVERAGE SLOPE of that sine curve across the item's own unscaled
 * bounds — i.e. the derivative integrated between the item's two edges. That
 * makes the result width-aware and keeps neighbouring items continuous no matter
 * how different their sizes are.
 */

export function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * @param pointerPosition Pointer coordinate on the dock's active axis (clientX
 *   when horizontal, clientY when vertical).
 * @param itemCenter      Centre of the item on the same axis.
 * @param itemSize        The item's *unscaled* size on that axis. Using the base
 *   size (not the currently rendered size) keeps the function stable while the
 *   item is mid-animation, otherwise the wave feeds back into itself.
 * @param maxRange        How far the magnification reaches, in px.
 * @param maxScale        Scale at the crest of the wave.
 */
export function calculateHoverScale(
  pointerPosition: number,
  itemCenter: number,
  itemSize: number,
  maxRange: number,
  maxScale: number,
): number {
  const centerDistance = Math.abs(pointerPosition - itemCenter);
  if (centerDistance >= maxRange || itemSize <= 0 || maxRange <= 0 || maxScale <= 1) {
    return 1;
  }

  // Signed distances from the pointer to each edge of the item.
  const d1 = itemCenter - itemSize / 2 - pointerPosition;
  const d2 = itemCenter + itemSize / 2 - pointerPosition;

  // Half a sine period spans 2 * maxRange, so the angular factor is PI/(2*range).
  const angleFactor = Math.PI / (2 * maxRange);

  // Normalisation keeps the crest exactly at `maxScale` regardless of item size.
  const normalization = 2 * Math.sin((angleFactor * itemSize) / 2);
  if (Math.abs(normalization) < Number.EPSILON) return 1;

  const maxSize = itemSize * maxScale;
  const averageSineSlope =
    (Math.sin(angleFactor * d2) - Math.sin(angleFactor * d1)) / (d2 - d1);
  const scale = 1 + ((maxSize - itemSize) / normalization) * averageSineSlope;

  return clamp(scale, 1, maxScale);
}

/** True when the pointer is inside the rect (inclusive bounds). */
export function isPointInsideRect(rect: DOMRect, x: number, y: number): boolean {
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}
